/**
 * Treffer der Gesamtsuche (`GET /protected/contacts/search`) als einheitliche
 * Ergebniszeilen. Kundenfeedback 18.10.2026 (XII): „Im Ergebnis steht neben
 * der Adresse, ob eigener Kontakt oder registriert.“
 *
 * Reine Hilfslogik ohne Vue-Abhängigkeit (testbar, wiederverwendbar z. B.
 * im Terminpartner-Picker). Alle Funktionen liefern neue Objekte.
 */
import type { Contact } from "@/services/contactModel";
import { contactRefusesKarsInvitations, getContactDisplayName, karsInvitationsOptOutByMailLabel } from "@/services/contactModel";
import type { Insurance } from "@/services/contactsApi";
import type { ContactSearchResponse } from "@/services/contactSearchApi";
import { roleForContactType } from "@/pages/pageCases/pageCaseDetail/sections/participantCategories";
import {
  type PortalEntry,
  defaultRoleForPortalEntry,
  flattenPortalOrganizations,
  portalEntryEmail,
  portalEntryEmails,
} from "@/pages/pageCases/pageCaseDetail/sections/portalOrganizations";

/** own = Adressbuch, registered = in KARS registriert, insurance = Versicherungsverzeichnis */
export type ContactHitSource = "own" | "registered" | "insurance";

export interface ContactSearchHit {
  /** Eindeutiger Schlüssel (v-for, Auswahl, Drag&Drop) */
  key: string;
  source: ContactHitSource;
  name: string;
  /** Zusatz: Ansprechpartner, Abteilung bzw. „Zentrale“ */
  detail: string;
  /** PLZ und Ort */
  city: string;
  email: string;
  /** Vorbelegte Fall-Rolle (Kontakttyp bzw. Unternehmensrolle), frei änderbar */
  defaultRole: string;
  isFavorite: boolean;
  favoriteRole: string | null;
  /** Eigener Kontakt, der zugleich mit einer registrierten Organisation verknüpft ist */
  isPortalLinked: boolean;
  /** Kundenfeedback 02.11.2026 (I): eigener Kontakt möchte keine KARS-Einladungen */
  refusesInvitations?: boolean;
  /** Kundenfeedback 03.11.2026 (I): Vermerk „per E-Mail abgemeldet am …“, wenn die Adresse sich selbst abgemeldet hat */
  optedOutByMailLabel?: string;
  contact?: Contact;
  portal?: PortalEntry;
  insurance?: Insurance;
}

function trimmed (value: string | null | undefined): string {
  return (value ?? "").trim();
}

function joinCity (zip: string | null | undefined, city: string | null | undefined): string {
  return [trimmed(zip), trimmed(city)].filter(Boolean).join(" ");
}

function personName (c: Contact): string {
  return [trimmed(c.firstName), trimmed(c.lastName)].filter(Boolean).join(" ");
}

export function contactToHit (contact: Contact): ContactSearchHit {
  const person = personName(contact);
  const company = trimmed(contact.companyName);
  return {
    key: `own-${contact.id ?? getContactDisplayName(contact)}`,
    source: "own",
    name: getContactDisplayName(contact),
    detail: company && person ? person : trimmed(contact.responsibility),
    city: joinCity(contact.zip, contact.city),
    email: trimmed(contact.email),
    defaultRole: trimmed(contact.favoriteRole) || roleForContactType(contact.contactType),
    isFavorite: !!contact.isFavorite,
    favoriteRole: trimmed(contact.favoriteRole) || null,
    isPortalLinked: !!contact.isPortalLinked || contact.idLinkedDepartment != null,
    refusesInvitations: contactRefusesKarsInvitations(contact),
    optedOutByMailLabel: karsInvitationsOptOutByMailLabel(contact) || undefined,
    contact,
  };
}

/**
 * Dublettenhinweis (Kundenfeedback 03.11.2026, I: „Wenn ich selbst einen
 * Kontakt angelegt hatte, versteht KARS, dass das möglicherweise derselbe
 * Kontakt ist?“): eigener Kontakt mit derselben E-Mail-Adresse (auch in
 * email2/email3), unabhängig von Groß-/Kleinschreibung. null = keiner.
 */
export function findContactByEmail (contacts: readonly Contact[], email: string | null | undefined): Contact | null {
  const wanted = trimmed(email).toLowerCase();
  if (!wanted || !wanted.includes("@")) { return null; }
  return contacts.find((c) => c.isActive !== false && [c.email, c.email2, c.email3].some((e) => trimmed(e).toLowerCase() === wanted)) ?? null;
}

export function portalToHit (entry: PortalEntry): ContactSearchHit {
  return {
    key: `reg-${entry.key}`,
    source: "registered",
    name: entry.companyName,
    detail: entry.isCentral ? entry.departmentName : [entry.locationName, entry.departmentName].filter(Boolean).join(" – "),
    city: entry.locationCity ?? "",
    email: portalEntryEmail(entry),
    defaultRole: defaultRoleForPortalEntry(entry),
    isFavorite: false,
    favoriteRole: null,
    isPortalLinked: false,
    portal: entry,
  };
}

export function insuranceToHit (insurance: Insurance): ContactSearchHit {
  return {
    key: `ins-${insurance.id}`,
    source: "insurance",
    name: trimmed(insurance.name) || "Versicherung",
    detail: trimmed(insurance.contactPerson),
    city: joinCity(insurance.zip, insurance.city),
    email: trimmed(insurance.email),
    defaultRole: "Versicherung",
    isFavorite: false,
    favoriteRole: null,
    isPortalLinked: false,
    insurance,
  };
}

/**
 * Registrierte Treffer, die bereits als eigener Kontakt vorhanden sind
 * (Portal-Verknüpfung auf die Abteilung oder gleiche E-Mail), entfallen –
 * der eigene Kontakt steht dann mit dem Zusatz „In KARS registriert“.
 */
export function withoutKnownPortalEntries (entries: readonly PortalEntry[], own: readonly Contact[]): PortalEntry[] {
  const linkedDepartments = new Set(own.map((c) => c.idLinkedDepartment).filter((id): id is number => id != null));
  const ownEmails = new Set(own.map((c) => trimmed(c.email).toLowerCase()).filter(Boolean));
  return entries.filter((e) => {
    if (e.departmentId != null && linkedDepartments.has(e.departmentId)) { return false; }
    return !portalEntryEmails(e).some((mail) => ownEmails.has(mail));
  });
}

/** Verzeichnis-Dubletten (gleicher Name und gleiche E-Mail) nur einmal zeigen. */
export function uniqueInsurances (insurances: readonly Insurance[]): Insurance[] {
  const seen = new Set<string>();
  return insurances.filter((i) => {
    const id = `${trimmed(i.name).toLowerCase()}|${trimmed(i.email).toLowerCase()}`;
    if (seen.has(id)) { return false; }
    seen.add(id);
    return true;
  });
}

/** Treffer nach Herkunft gruppiert (eigene Kontakte, registriert, Verzeichnis). */
export function groupHitsBySource (hits: readonly ContactSearchHit[]): { source: ContactHitSource; label: string; hits: ContactSearchHit[] }[] {
  const order: { source: ContactHitSource; label: string }[] = [
    { source: "own", label: "Eigene Kontakte" },
    { source: "registered", label: "In KARS registriert" },
    { source: "insurance", label: "Versicherungsverzeichnis" },
  ];
  return order
    .map((g) => ({ ...g, hits: hits.filter((h) => h.source === g.source) }))
    .filter((g) => g.hits.length > 0);
}

/** Ergebnis der Gesamtsuche als Zeilen: eigene Kontakte, registrierte, Verzeichnis. */
export function buildContactSearchHits (response: ContactSearchResponse | null | undefined, knownContacts: readonly Contact[] = []): ContactSearchHit[] {
  if (!response) { return []; }
  const own = Array.isArray(response.own) ? response.own : [];
  const portal = withoutKnownPortalEntries(flattenPortalOrganizations(response.registered), [...own, ...knownContacts]);
  const insurances = uniqueInsurances(Array.isArray(response.insurances) ? response.insurances : []);
  return [
    ...own.map((c) => contactToHit(c)),
    ...portal.map((e) => portalToHit(e)),
    ...insurances.map((i) => insuranceToHit(i)),
  ];
}

/** Anzeigename der Quelle als Badge-Text. */
export function hitSourceLabel (hit: Pick<ContactSearchHit, "source">): string {
  if (hit.source === "own") { return "Eigener Kontakt"; }
  if (hit.source === "registered") { return "In KARS registriert"; }
  return "Versicherungsverzeichnis";
}
